<template>
    <b-container class="atmo-nested-section">
        <h3 class="grey-color font-weight-bold mb-3">Создание дополнительного соглашения</h3>

        <b-overlay :show="isLoading" opacity="0.6" rounded spinner-variant="primary">
            <h5 class="grey-color fw-600">Измените спецификацию</h5>
            <b-form-group>
                <fl44-contract-supplementary-agreement-regular-specifications-edit-table v-model="form.deliverables" :v="$v.form.deliverables" :is-loading="isLoading" />
                <b-form-invalid-feedback :state="!$v.form.deliverables.$invalid" class="mt-3">{{ specErrorMessage }}</b-form-invalid-feedback>
                <text-row-price :label-cols-lg="4" label="Стоимость контракта" :value="pivc.purchaseitem.contract.price_total" />
                <text-row-price :label-cols-lg="4" label="Стоимость контракта с учетом дополнительного соглашения" :value="specSum" />
                <form-row-edit-date :label-cols-lg="4" label="Дата заключения дополнительного соглашения" v-model="form.supplementary_agreement_date" :v="$v.form.supplementary_agreement_date" :min-date="purchase.contract_date" :max-date="$now()" compact />
                <form-row-single-line-document-upload v-model="form.supplementary_agreement_attachment" :label-cols-lg="4" :v="$v.form.supplementary_agreement_attachment" title="Дополнительное соглашение" compact />
            </b-form-group>
        </b-overlay>
        <b-overlay :show="isLoading" class="d-inline-block" opacity="0.6" rounded spinner-small spinner-variant="primary">
            <b-button variant="custom-outline-secondary" class="text-uppercase mt-1" @click="onCancel">Отмена</b-button>
            <b-button variant="custom-green" class="text-uppercase mt-1 ml-2" :disabled="$v.$invalid" @click="createSupplementaryAgreement">Отправить дополнительное соглашение поставщику</b-button>
        </b-overlay>
    </b-container>
</template>

<script>
import { minLength, minValue, required } from 'vuelidate/lib/validators';
import roundToTwoDecimals from '@lib/js/src/misc/roundToTwoDecimals';
import Fl44ContractSupplementaryAgreementRegularSpecificationsEditTable from '@/components/products/fl44/partials/fl44-contract-supplementary-agreement-regular-specifications-edit-table.vue';

export default {
    name: 'fl44-contract-supplementary-agreement-regular-create',
    components: { Fl44ContractSupplementaryAgreementRegularSpecificationsEditTable },
    props: {
        purchase: {
            type: Object
        },
        pivc: {
            type: Object
        }
    },
    data() {
        return {
            isLoading: false,
            price: null,
            priceLimit: null,
            form: {
                supplementary_agreement_date: null,
                supplementary_agreement_attachment: null,
                type: 'regular',
                deliverables: []
            }
        };
    },
    validations() {
        return {
            form: {
                supplementary_agreement_date: {
                    required
                },
                supplementary_agreement_attachment: {
                    required
                },
                deliverables: {
                    required,
                    minLength: minLength(1),
                    priceLimit() {
                        return this.specSum <= this.priceLimit;
                    },
                    $each: {
                        ktru_item_id: { required },
                        amount: { required, minValue: minValue(0) },
                        minAmount(item, arr) {
                            const items = arr.filter((element) => element.purchase_item_id === item.purchase_item_id);
                            const sum = items.reduce((sum, element) => sum + element.amount * 1, 0);
                            return sum >= item.min_amount;
                        },
                        maxAmount(item, arr) {
                            const items = arr.filter((element) => element.purchase_item_id === item.purchase_item_id);
                            const sum = items.reduce((sum, element) => sum + element.amount * 1, 0);
                            return sum <= item.max_amount;
                        },
                        price: {
                            required,
                            minValue: minValue(0.01)
                        },
                        maxPrice(item) {
                            return item.price <= item.max_price;
                        }
                    }
                }
            }
        };
    },
    mounted() {
        this.fillData();
    },
    methods: {
        onCancel() {
            this.$emit('cancel');
        },
        async fillData() {
            this.isLoading = true;
            await this.$api.personal.fl44
                .getSupplementaryAgreementRegularTemplateData(this.purchase.id)
                .then((response) => {
                    this.form.deliverables = response.deliverables;
                    this.contract_conclusion_date = response.contract_conclusion_date;
                    this.price = response.price;
                    this.priceLimit = response.price_limit;
                })
                .catch(() => {
                    this.$emit('cancel');
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        async createSupplementaryAgreement() {
            this.isLoading = true;
            await this.$api.personal.fl44
                .sendSupplementaryAgreementToSupplier(this.purchase.id, this.form)
                .then(() => {
                    this.$emit('done');
                })
                .finally(() => {
                    this.isLoading = false;
                });
        }
    },
    computed: {
        specSum() {
            return roundToTwoDecimals(
                this.form.deliverables.reduce((sum, item) => {
                    return (sum += roundToTwoDecimals(item.amount * item.price));
                }, 0)
            );
        },
        specErrorMessage() {
            const deliverables = Object.values(this.$v.form.deliverables.$each.$iter);
            if (deliverables.some((e) => e.amount.$invalid)) return 'У одной или более позиций в таблице спецификации не заполнено поле "Количество"';
            if (deliverables.some((e) => e.price.$invalid)) return 'У одной или более позиций в таблице спецификации не заполнено поле "Цена"';
            if (deliverables.some((e) => !e.maxPrice)) return 'Цена единицы ТРУ в спецификации не может быть больше указанной в контракте.';
            if (deliverables.some((e) => !e.maxAmount)) return 'Максимальное увеличение количества может составлять не более 10%.';
            if (deliverables.some((e) => !e.minAmount)) return 'Максимальное снижение количества может составлять не более 10%.';
            if (!this.$v.form.deliverables.priceLimit) return `Превышена максимальная сумма закупки в ${this.$formatPrice(this.priceLimit)} руб.`;
            return '';
        }
    }
};
</script>
